import React from "react";

import {PlusIcon} from "@heroicons/react/24/outline";
import Decimal from "decimal.js";

import * as typography from "@client/shared/typography";
import * as utils from "@client/utils";
import {classes} from "@client/utils";

import {Icon} from "@client/components/Icon";
import {Image} from "@client/components/Image";
import {ManagerLayout} from "@client/site/ManagerLayout";
import * as forms from "@client/site/forms";
import {reverse, server} from "@reactivated";

const styles = {
    noData: "client-dashboard-no-data",
    deltaValue: "",
    success: "client-dashboard-success",
    failure: "client-dashboard-failure",
    status: "flex justify-center",
    metricStatus: "flex items-center gap-2.5",
    spacer: "space-y-[20px]",
    empty: "client-dashboard-empty border-2 border-dashed border-gray-300 rounded-lg flex flex-col justify-center items-center py-[4vh]",
};

export const NoData = () => (
    <div className={styles.noData}>
        <div />
    </div>
);

export const Delta = ({
    valueAndType,
    isImperial,
}: {
    valueAndType: {type: "percent" | "weight" | "length"; value: string | null} | null;
    isImperial: boolean;
}) => {
    const noData = (
        <div className="ml-4">
            <NoData />
        </div>
    );
    if (valueAndType == null) {
        return noData;
    }
    const {value, type} = valueAndType;

    if (value == null) {
        return noData;
    }

    const casted = new Decimal(value).toDecimalPlaces(1);
    const formatted = (
        <div className={styles.deltaValue}>
            {utils.formatDecimalAndUnit(casted.abs(), type, isImperial)}
        </div>
    );

    const deltaClasses = classes({});

    const icon = casted.isZero()
        ? "empty"
        : casted.isPositive()
          ? "custom-thick-plus"
          : "custom-thick-minus";
    return (
        <div className={deltaClasses}>
            <Icon large="small" minimalSpace icon={icon}>
                {formatted}
            </Icon>
        </div>
    );
};

export const formatStatus = (status: "success" | "failure" | "na") => {
    const {icon, color} = (() => {
        if (status === "success") {
            return {
                icon: <Icon icon="custom-bolder-check" />,
                color: styles.success,
            };
        } else if (status === "failure") {
            return {
                icon: <Icon icon="custom-bolder-cross" />,
                color: styles.failure,
            };
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        } else if (status === "na") {
            return {icon: <NoData />, color: null};
        }
        const _exhaust: never = status;
        return _exhaust;
    })();
    return <div className={classes(styles.status, color)}>{icon}</div>;
};

export const WorkoutStatus = (props: {status: boolean}) => {
    const statusIcon = props.status ? formatStatus("success") : formatStatus("na");
    return (
        <div className="flex items-center gap-1 py-0.5">
            <span className="mr-1">{statusIcon}</span>
            {props.status ? "Worked out" : "No workout"}
        </div>
    );
};

export const HabitStatus = ({
    total,
    target,
    status,
    habit,
    noStatus,
}: {
    status: "success" | "failure" | "na";
    habit: "SLEEP" | "STEPS" | "WATER" | "GRATITUDE";
    target: number | null;
    total: string | null;
    noStatus?: boolean;
}) => {
    const statusIcon = noStatus === true ? formatStatus("na") : formatStatus(status);
    const numberFormatter = new Intl.NumberFormat("en-US");

    if (target == null && total == null) {
        return null;
    }

    const {formattedTarget, formattedTotal, formattedUnit} = (() => {
        if (habit == "SLEEP") {
            return {
                formattedTotal: utils.convertSecondsToHoursMinutes(
                    total == null ? 0 : parseInt(total),
                ),
                formattedTarget:
                    target == null ? null : utils.convertSecondsToHoursMinutes(target),
                formattedUnit: "sleep",
            };
        } else if (habit == "STEPS") {
            return {
                formattedTotal: numberFormatter.format(
                    total == null ? 0 : parseInt(total),
                ),
                formattedTarget: target == null ? null : numberFormatter.format(target),
                formattedUnit: "steps",
            };
        } else if (habit == "WATER") {
            return {
                formattedTotal: total == null ? 0 : parseInt(total),
                formattedTarget: target,
                formattedUnit: "cups of water",
            };
        }
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        else if (habit == "GRATITUDE") {
            return {
                formattedTotal: "Shared gratitude",
                formattedTarget: null,
                formattedUnit: null,
            };
        }
        return habit;
    })();

    return (
        <div className="flex items-center gap-1 py-0.5">
            <span className="mr-1">{statusIcon}</span>
            {formattedTotal}
            {formattedTarget != null && <span> / {formattedTarget}</span>}
            <span className="text-gray-700">{formattedUnit}</span>
        </div>
    );
};

export const MetricStatus = ({
    total,
    target,
    status,
    unit,
    noStatus,
}: {
    status: "success" | "failure" | "na";
    unit: "cal" | "g" | "mg" | "IU";
    target: string | null;
    total: string | null;
    noStatus?: boolean;
}) => {
    if (total == null && target == null) {
        return <NoData />;
    }

    const casted = new Decimal(total ?? "0").toDecimalPlaces(0);

    const statusIcon = noStatus === true ? formatStatus("na") : formatStatus(status);

    if (target == null) {
        // const formatted = `${casted.toString()} / - ${unit}`;
        const formatted = `${casted.toString()} ${unit}`;
        return (
            <div className={styles.metricStatus}>
                {statusIcon} <div>{formatted}</div>
            </div>
        );
    } else {
        const castedTarget = new Decimal(target).toDecimalPlaces(0);
        const formatted = `${casted.toString()} / ${castedTarget.toString()} ${unit}`;

        return (
            <div className={styles.metricStatus}>
                {statusIcon} <div>{formatted}</div>
            </div>
        );
    }
};

/**
 * Special version that right aligns the actual value with the delta (left
 * aligned) which looks nicer.
 *
 * It merges columns and if both delta and value are missing, only a single
 * <NoData /> is shown.
 */
export const PrimaryMetric = ({
    metric,
    children,
    isImperial,
}: {
    metric: server.manager.templates.ClientDashboard["clients"][number]["primary_metric"];
    children: React.ReactElement;
    isImperial: boolean;
}) => {
    if (metric?.value == null) {
        return (
            <td
                colSpan={2}
                className="relative px-3 py-4 text-sm whitespace-nowrap text-gray-500"
            >
                {children}
                <div className="ml-2">
                    <NoData />
                </div>
            </td>
        );
    }

    const value = utils.formatDecimalAndUnit(
        new Decimal(metric.value).toDecimalPlaces(1),
        metric.metric.type,
        isImperial,
    );

    if (metric.delta == null) {
        return (
            <td
                colSpan={2}
                className="relative py-4 pl-3 text-sm whitespace-nowrap text-gray-500"
            >
                {children}
                {value}
            </td>
        );
    }
    return (
        <>
            <td className="relative py-4 pl-3 text-sm whitespace-nowrap text-gray-500">
                {children}
                {value}
            </td>
            <td className="relative py-4 pl-2 text-sm whitespace-nowrap text-gray-500">
                {React.cloneElement(children)}
                <Delta
                    valueAndType={{
                        value: metric.delta,
                        type: metric.metric.type,
                    }}
                    isImperial={isImperial}
                />
            </td>
        </>
    );
};

const Invited = (props: server.manager.templates.ClientDashboard) => {
    return (
        <div className="mt-8 flow-root">
            <div
                className="
                    -mx-4 -my-2 overflow-x-auto
                    sm:-mx-6
                    lg:-mx-8
                "
            >
                <div
                    className="
                        inline-block min-w-full py-2 align-middle
                        sm:px-6
                        lg:px-8
                    "
                >
                    <div
                        className="
                            overflow-hidden shadow-sm ring-1 ring-black/5
                            sm:rounded-lg
                        "
                    >
                        <table
                            className="
                                min-w-full divide-y divide-gray-300 whitespace-nowrap
                            "
                        >
                            <thead className="bg-white">
                                <tr>
                                    <th
                                        scope="col"
                                        className="
                                            py-3.5 pr-3 pl-4 text-left text-sm
                                            font-semibold text-gray-900
                                            sm:pl-6
                                        "
                                    >
                                        Name
                                    </th>
                                    <th
                                        scope="col"
                                        className="
                                            px-3 py-3.5 text-left text-sm font-semibold
                                            text-gray-900
                                        "
                                    >
                                        Invited
                                    </th>
                                    <th
                                        scope="col"
                                        className="
                                            px-3 py-3.5 text-left text-sm font-semibold
                                            text-gray-900
                                        "
                                    >
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                                {props.invited.map((client_manager) => (
                                    <tr key={client_manager.id} className="relative">
                                        <td
                                            className="
                                                py-4 pr-3 pl-4 text-sm whitespace-nowrap
                                                text-gray-900
                                                sm:pl-6
                                            "
                                        >
                                            <div className="flex items-center">
                                                <div>
                                                    <div
                                                        className="
                                                            font-medium text-gray-900
                                                        "
                                                    >
                                                        {client_manager.name}
                                                    </div>
                                                    <div className="mt-1 text-gray-500">
                                                        {client_manager.client.email}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>

                                        <td
                                            className="
                                                px-3 py-4 text-sm whitespace-nowrap
                                                text-gray-500
                                            "
                                        >
                                            {utils.formatDistanceToNow(
                                                client_manager.created_at,
                                            )}{" "}
                                            ago
                                        </td>

                                        <td
                                            className="
                                                space-x-2 px-3 py-4 text-sm
                                                whitespace-nowrap text-gray-500
                                            "
                                        >
                                            <button
                                                type="submit"
                                                name="resend"
                                                className="
                                                    rounded-sm bg-white px-2 py-1
                                                    text-xs font-semibold text-gray-900
                                                    shadow-xs ring-1 ring-gray-300
                                                    ring-inset
                                                    hover:bg-gray-50
                                                "
                                                value={client_manager.id.toString()}
                                            >
                                                Re-send invite
                                            </button>
                                            <a
                                                className="
                                                    rounded-sm bg-white px-2 py-1
                                                    text-xs font-semibold text-gray-900
                                                    shadow-xs ring-1 ring-gray-300
                                                    ring-inset
                                                    hover:bg-gray-50
                                                "
                                                href={reverse("any_client_delete", {
                                                    client_manager_id:
                                                        client_manager.id,
                                                })}
                                            >
                                                Delete
                                            </a>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

const BillingLabel = ({
    clientManager,
}: {
    clientManager: server.manager.templates.ClientDashboard["clients"][number]["client_manager"];
}) => {
    if (clientManager.type === "MANAGER_PAYS") {
        return null;
    }
    if (clientManager.client.is_subscription_active) {
        return (
            <span className="ml-2 text-xs font-normal text-gray-400">Client pays</span>
        );
    }
    return (
        <span className="ml-2 text-xs font-normal text-red-400">Client canceled</span>
    );
};

export const Table = (props: server.manager.templates.ClientDashboard) => {
    const hasMixedBilling =
        props.clients.some((c) => c.client_manager.type === "MANAGER_PAYS") &&
        props.clients.some((c) => c.client_manager.type === "CLIENT_PAYS");

    return (
        <div className="mt-8 flow-root">
            <div
                className="
                    -mx-4 -my-2 overflow-x-auto
                    sm:-mx-6
                    lg:-mx-8
                "
            >
                <div
                    className="
                        inline-block min-w-full py-2 align-middle
                        sm:px-6
                        lg:px-8
                    "
                >
                    <div
                        className="
                            overflow-hidden shadow-sm ring-1 ring-black/5
                            sm:rounded-lg
                        "
                    >
                        <table
                            className="
                                min-w-full divide-y divide-gray-300 whitespace-nowrap
                            "
                        >
                            <thead className="bg-white">
                                <tr>
                                    <th
                                        scope="col"
                                        className="
                                            py-3.5 pr-3 pl-4 text-left text-sm
                                            font-semibold text-gray-900
                                            sm:pl-6
                                        "
                                    >
                                        Name
                                    </th>
                                    <th
                                        scope="col"
                                        className="
                                            px-3 py-3.5 text-left text-sm font-semibold
                                            text-gray-900
                                        "
                                    >
                                        Last seen
                                    </th>

                                    <th
                                        scope="col"
                                        className="
                                            px-3 py-3.5 text-left text-sm font-semibold
                                            text-gray-900
                                        "
                                        colSpan={2}
                                    >
                                        {props.clients[0].primary_metric?.metric
                                            .label ?? "N/A"}
                                    </th>

                                    <th
                                        scope="col"
                                        className="
                                            px-3 py-3.5 text-left text-sm font-semibold
                                            text-gray-900
                                        "
                                    >
                                        {props.clients[0].secondary_metric?.metric
                                            .label ?? "N/A"}
                                    </th>

                                    <th
                                        scope="col"
                                        className="
                                            px-3 py-3.5 text-left text-sm font-semibold
                                            text-gray-900
                                        "
                                    >
                                        {props.clients[0].tertiary_metric?.metric
                                            .label ?? "N/A"}
                                    </th>

                                    {props.clients[0].statuses.map((status, index) => (
                                        <th
                                            scope="col"
                                            key={index}
                                            className="
                                                px-0 py-3.5 text-sm font-semibold
                                                text-gray-900
                                            "
                                        >
                                            <div>
                                                {status.date != null &&
                                                    utils.formatShortMonthAndDay(
                                                        status.date,
                                                    )}
                                            </div>
                                        </th>
                                    ))}
                                    <th aria-hidden className="pr-4" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                                {props.clients.map(({client_manager, ...snapshot}) => (
                                    <tr
                                        key={client_manager.id}
                                        className="hover:bg-indigo-100/20"
                                    >
                                        <td
                                            className="
                                                relative py-4 pr-3 pl-4 text-sm
                                                whitespace-nowrap text-gray-900
                                                sm:pl-6
                                            "
                                        >
                                            <a
                                                aria-hidden={true}
                                                className="absolute inset-0"
                                                href={reverse("client_detail", {
                                                    client_manager_id:
                                                        client_manager.id,
                                                })}
                                            />
                                            <div className="flex items-center">
                                                <div>
                                                    <div
                                                        className="
                                                            font-medium text-gray-900
                                                        "
                                                    >
                                                        <a
                                                            href={reverse(
                                                                "client_detail",
                                                                {
                                                                    client_manager_id:
                                                                        client_manager.id,
                                                                },
                                                            )}
                                                        >
                                                            {client_manager.name}
                                                        </a>
                                                        {hasMixedBilling && (
                                                            <BillingLabel
                                                                clientManager={
                                                                    client_manager
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                    <div className="mt-1 text-gray-500">
                                                        {client_manager.client.email}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>

                                        <td
                                            className="
                                                relative px-3 py-4 text-sm
                                                whitespace-nowrap text-gray-500
                                            "
                                        >
                                            <a
                                                aria-hidden={true}
                                                className="absolute inset-0"
                                                href={reverse("client_detail", {
                                                    client_manager_id:
                                                        client_manager.id,
                                                })}
                                            />
                                            {utils.formatDistanceToNow(
                                                snapshot.last_seen,
                                            )}{" "}
                                            ago
                                        </td>

                                        <PrimaryMetric
                                            metric={snapshot.primary_metric}
                                            isImperial={
                                                client_manager.client.profile
                                                    .is_imperial
                                            }
                                        >
                                            <a
                                                aria-hidden={true}
                                                className="absolute inset-0"
                                                href={reverse("client_detail", {
                                                    client_manager_id:
                                                        client_manager.id,
                                                })}
                                            />
                                        </PrimaryMetric>
                                        <td
                                            className="
                                                relative px-3 py-4 text-sm
                                                whitespace-nowrap text-gray-500
                                            "
                                        >
                                            <a
                                                aria-hidden={true}
                                                className="absolute inset-0"
                                                href={reverse("client_detail", {
                                                    client_manager_id:
                                                        client_manager.id,
                                                })}
                                            />
                                            <Delta
                                                isImperial={
                                                    client_manager.client.profile
                                                        .is_imperial
                                                }
                                                valueAndType={
                                                    snapshot.secondary_metric != null
                                                        ? {
                                                              value: snapshot
                                                                  .secondary_metric
                                                                  .delta,
                                                              type: snapshot
                                                                  .secondary_metric
                                                                  .metric.type,
                                                          }
                                                        : null
                                                }
                                            />
                                        </td>
                                        <td
                                            className="
                                                relative px-3 py-4 text-sm
                                                whitespace-nowrap text-gray-500
                                            "
                                        >
                                            <a
                                                aria-hidden={true}
                                                className="absolute inset-0"
                                                href={reverse("client_detail", {
                                                    client_manager_id:
                                                        client_manager.id,
                                                })}
                                            />
                                            <Delta
                                                isImperial={
                                                    client_manager.client.profile
                                                        .is_imperial
                                                }
                                                valueAndType={
                                                    snapshot.tertiary_metric != null
                                                        ? {
                                                              value: snapshot
                                                                  .tertiary_metric
                                                                  .delta,
                                                              type: snapshot
                                                                  .tertiary_metric
                                                                  .metric.type,
                                                          }
                                                        : null
                                                }
                                            />
                                        </td>

                                        {snapshot.statuses.map((status, index) => (
                                            <td
                                                key={index}
                                                className="
                                                    relative px-3 py-4 text-sm
                                                    whitespace-nowrap text-gray-500
                                                "
                                            >
                                                <a
                                                    aria-hidden={true}
                                                    className="absolute inset-0"
                                                    href={reverse("client_detail", {
                                                        client_manager_id:
                                                            client_manager.id,
                                                    })}
                                                />
                                                {formatStatus(status.status)}
                                            </td>
                                        ))}
                                        <td aria-hidden className="relative pr-4">
                                            <a
                                                aria-hidden={true}
                                                className="absolute inset-0"
                                                href={reverse("client_detail", {
                                                    client_manager_id:
                                                        client_manager.id,
                                                })}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const Template = (props: server.manager.templates.ClientDashboard) => {
    const addClient = (
        <typography.Button href={reverse("business_create")}>
            <PlusIcon className="mr-1.5 -ml-0.5 size-5" aria-hidden="true" />
            Add Client
        </typography.Button>
    );
    return (
        <ManagerLayout title="Client Dashboard" currentClientManagerId={null}>
            <form className={styles.spacer} noValidate method="POST" action="">
                <forms.CSRFToken />
                <div className="flex items-center">
                    <div className="flex-1">
                        <h1 className="text-2xl/6 text-gray-900">Clients</h1>
                    </div>
                    <div className="">{addClient}</div>
                </div>
                {props.clients.length > 0 ? (
                    <Table {...props} />
                ) : (
                    <div className={styles.empty}>
                        <Image image="empty-clients" title="People" />
                        <h3 className="mt-4 text-sm font-semibold text-gray-900">
                            You have no active clients
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                            Get started by inviting a client.
                        </p>
                        <div className="mt-4">{addClient}</div>
                    </div>
                )}
                <h1 className="text-2xl/6 text-gray-900">Invited</h1>
                {props.invited.length > 0 ? (
                    <Invited {...props} />
                ) : (
                    <div className={styles.empty}>
                        <Image image="empty-invitations" title="Invitees" />
                        <h3 className="mt-4 text-sm font-semibold text-gray-900">
                            You have no pending invitations
                        </h3>
                        <div className="mt-4">{addClient}</div>
                    </div>
                )}
            </form>
        </ManagerLayout>
    );
};
